import { BleTransport } from '../ble/transport';
import type { SweepPoint } from '../ble/toolbox-protocol';
import type { HardwareConfig, HardwareRevisionConfig } from '../hardware-config/schema';
import {
  type OperationalTuningTargetConfig,
  RetuneConfigError,
  resolveRetuneRevision,
  resolveRetuneTargetById,
} from './retune-target-selection';
import { runSweep, type SweepResult } from './sweep-runner';

export const FREQUENCY_MIN_HZ = 20000;
export const FREQUENCY_MAX_HZ = 60000;

export const COARSE_PEAK_TIE_THRESHOLD_W = 1.0;
export const FINE_PEAK_TIE_THRESHOLD_W = 0.25;
export const RETUNE_DEFAULT_CENTER_HZ = 40000;
const RETUNE_COARSE_STARTUP_DISCARD_POINT_COUNT = 3;
export type RetuneSweepPhase = 'coarse' | 'fine';

export type RetuneSweepSpec = {
  startHz: number;
  endHz: number;
  stepHz: number;
  dwellMs: number;
};

export type RetuneResult = {
  originalCenterHz: number;
  coarsePeakHz: number;
  finePeakHz: number;
  coarseSpec: RetuneSweepSpec;
  fineSpec: RetuneSweepSpec;
  coarseSweep: SweepResult;
  fineSweep: SweepResult;
  revisionId: string;
  targetId: string;
  targetSnapshot: OperationalTuningTargetConfig;
};

export type RetuneConfigInput = {
  hardwareConfig?: HardwareConfig | null;
  hardwareRevision?: string | null;
  revision?: HardwareRevisionConfig | null;
  selectedTargetId: string | null;
  startingCenterHz?: number | null;
};

export type RetuneOptions = {
  signal?: AbortSignal;
  onStatus?: (message: string) => void;
  onDebug?: (message: string, details?: Record<string, unknown>) => void;
  onPhaseChange?: (phase: RetuneSweepPhase) => void;
  onPauseChange?: (phase: RetuneSweepPhase, paused: boolean) => void;
  onSweepPoint?: (phase: RetuneSweepPhase, point: SweepPoint) => void;
  onInitialFrequencyResolved?: (hz: number) => void;
  onSuggestedFrequencyResolved?: (hz: number) => void;
  shouldPause?: () => boolean;
};

type CoarseAnchorSource = 'first-peak' | 'max-peak' | 'max-peak-fallback';
type NormalizedExitCriteria = 'first_peak' | 'max_peak';

type CoarseAnchorSelection = {
  point: SweepPoint;
  source: CoarseAnchorSource;
  normalizedExitCriteria: NormalizedExitCriteria;
  unknownExitCriteriaFallback: boolean;
  thresholdW: number;
  qualifyingPointCount: number;
};

export async function runRetune(
  transport: BleTransport,
  configInput: RetuneConfigInput,
  options: RetuneOptions = {}
): Promise<RetuneResult> {
  const revision =
    configInput.revision ??
    resolveRetuneRevision({
      config: configInput.hardwareConfig,
      revision: configInput.hardwareRevision,
    });

  options.onDebug?.('Resolved hardware revision for retune', {
    detectedRevision: configInput.hardwareRevision ?? null,
    matchedRevisionId: revision.revision_id,
  });

  const centerSelection = await pickStartingCenterFrequency(transport, configInput.startingCenterHz);
  const center = centerSelection.centerHz;
  options.onInitialFrequencyResolved?.(center);
  options.onDebug?.('Selected initial center frequency', {
    centerHz: center,
    source: centerSelection.source,
  });
  if (centerSelection.source !== 'live-status') {
    options.onStatus?.(`Using center frequency ${formatFrequencyHz(center)} Hz (${centerSelection.source}).`);
  }

  const selectedTarget = {
    revision,
    target: resolveRetuneTargetById({
      revision,
      targetId: configInput.selectedTargetId,
    }),
    source: 'explicit-selection' as const,
  };

  options.onDebug?.('Retune target selected', {
    revisionId: selectedTarget.revision.revision_id,
    targetId: selectedTarget.target.target_id,
    targetSource: selectedTarget.source,
    currentCenterHz: center,
  });

  const coarseScanStartHz = requireFiniteHz(
    selectedTarget.target.initial_tuning.scan_start_freq_hz,
    selectedTarget.target.target_id,
    'scan_start_freq_hz'
  );
  const coarseScanEndHz = requireFiniteHz(
    selectedTarget.target.initial_tuning.scan_end_freq_hz,
    selectedTarget.target.target_id,
    'scan_end_freq_hz'
  );
  const coarseStepHz = requirePositiveHz(
    selectedTarget.target.initial_tuning.coarse_step_hz,
    selectedTarget.target.target_id,
    'coarse_step_hz'
  );
  const fineStepHz = requirePositiveHz(
    selectedTarget.target.initial_tuning.fine_step_hz,
    selectedTarget.target.target_id,
    'fine_step_hz'
  );
  const dwellMs = requirePositiveMilliseconds(
    selectedTarget.target.initial_tuning.dwell_time_ms,
    selectedTarget.target.target_id,
    'dwell_time_ms'
  );
  const fineBandwidthHz = requireNonNegativeHz(
    selectedTarget.target.initial_tuning.fine_scan_bandwidth_hz,
    selectedTarget.target.target_id,
    'fine_scan_bandwidth_hz'
  );
  const autotuneMinPeakPowerW =
    requireNonNegativeNumber(
      selectedTarget.target.autotune_params.min_peak_power_mw,
      selectedTarget.target.target_id,
      'autotune_params.min_peak_power_mw'
    ) / 1000;
  const coarseConfiguredStartHz = clampHz(coarseScanStartHz);
  const coarseConfiguredEndHz = clampHz(coarseScanEndHz);
  const coarseStartupPreroll = resolveSweepStartupPreroll({
    configuredStartHz: coarseConfiguredStartHz,
    configuredEndHz: coarseConfiguredEndHz,
    stepHz: coarseStepHz,
    desiredPointCount: RETUNE_COARSE_STARTUP_DISCARD_POINT_COUNT,
  });
  const coarseStartupPointSkipCount = coarseStartupPreroll.actualPointCount;
  let remainingCoarseLivePointsToSkip = coarseStartupPointSkipCount;
  const coarseSpec: RetuneSweepSpec = {
    startHz: coarseConfiguredStartHz,
    endHz: coarseConfiguredEndHz,
    stepHz: coarseStepHz,
    dwellMs,
  };

  options.onPhaseChange?.('coarse');
  options.onStatus?.('Starting coarse retune sweep...');
  const rawCoarseSweep = await runSweep(
    transport,
    {
      startHz: coarseStartupPreroll.sweepStartHz,
      endHz: coarseConfiguredEndHz,
      stepHz: coarseStepHz,
      dwellMs,
      timeoutMs: 60000,
    },
    {
      signal: options.signal,
      onStatus: options.onStatus,
      onDebug: options.onDebug,
      onPoint: (point) => {
        if (remainingCoarseLivePointsToSkip > 0) {
          remainingCoarseLivePointsToSkip -= 1;
          return;
        }
        options.onSweepPoint?.('coarse', point);
      },
      shouldPause: options.shouldPause,
      onPauseChange: (paused) => {
        options.onPauseChange?.('coarse', paused);
      },
    }
  );
  const coarseSweep = discardInitialSweepPoints(rawCoarseSweep, coarseStartupPointSkipCount);
  const discardedCoarsePointCount = rawCoarseSweep.points.length - coarseSweep.points.length;
  if (discardedCoarsePointCount > 0) {
    options.onDebug?.('Discarded initial coarse sweep points during retune startup filtering', {
      coarseStartupDiscardPointCount: RETUNE_COARSE_STARTUP_DISCARD_POINT_COUNT,
      coarseStartupPrerollPointCount: coarseStartupPreroll.actualPointCount,
      coarseConfiguredStartHz,
      coarseSweepStartHz: coarseStartupPreroll.sweepStartHz,
      configuredSkipPointCount: coarseStartupPointSkipCount,
      discardedPointCount: discardedCoarsePointCount,
      retainedPointCount: coarseSweep.points.length,
    });
  }

  const coarsePeakSelection = selectCoarseAnchorByExitCriteria({
    points: coarseSweep.points,
    referenceFrequencyHz: center,
    tieThresholdW: COARSE_PEAK_TIE_THRESHOLD_W,
    exitCriteria: selectedTarget.target.initial_tuning.exit_criteria,
    thresholdW: autotuneMinPeakPowerW,
  });
  if (coarsePeakSelection.unknownExitCriteriaFallback) {
    options.onDebug?.('Unknown retune exit criteria; falling back to max_peak', {
      exitCriteria: selectedTarget.target.initial_tuning.exit_criteria,
      normalizedExitCriteria: coarsePeakSelection.normalizedExitCriteria,
    });
  }
  const coarsePeak = coarsePeakSelection.point;
  options.onDebug?.('Selected coarse peak', {
    frequencyHz: coarsePeak.frequencyHz,
    powerW: coarsePeak.powerW,
    referenceCenterHz: center,
    tieThresholdW: COARSE_PEAK_TIE_THRESHOLD_W,
    coarseStartupDiscardPointCount: RETUNE_COARSE_STARTUP_DISCARD_POINT_COUNT,
    coarseStartupPrerollPointCount: coarseStartupPreroll.actualPointCount,
    discardedCoarsePointCount,
    exitCriteria: selectedTarget.target.initial_tuning.exit_criteria,
    normalizedExitCriteria: coarsePeakSelection.normalizedExitCriteria,
    coarsePeakSelectionSource: coarsePeakSelection.source,
    unknownExitCriteriaFallback: coarsePeakSelection.unknownExitCriteriaFallback,
    autotuneMinPeakPowerW: coarsePeakSelection.thresholdW,
    qualifyingCoarsePointCount: coarsePeakSelection.qualifyingPointCount,
  });

  options.onPhaseChange?.('fine');
  options.onStatus?.('Starting fine retune sweep...');
  const fineSpec: RetuneSweepSpec = {
    startHz: clampHz(coarsePeak.frequencyHz - fineBandwidthHz),
    endHz: clampHz(coarsePeak.frequencyHz + fineBandwidthHz),
    stepHz: fineStepHz,
    dwellMs,
  };
  const fineSweep = await runSweep(
    transport,
    {
      ...fineSpec,
      timeoutMs: 45000,
    },
    {
      signal: options.signal,
      onStatus: options.onStatus,
      onDebug: options.onDebug,
      onPoint: (point) => {
        options.onSweepPoint?.('fine', point);
      },
      shouldPause: options.shouldPause,
      onPauseChange: (paused) => {
        options.onPauseChange?.('fine', paused);
      },
    }
  );

  const finePeak = selectPeakByPower(fineSweep.points, coarsePeak.frequencyHz, FINE_PEAK_TIE_THRESHOLD_W);
  options.onDebug?.('Selected fine peak', {
    frequencyHz: finePeak.frequencyHz,
    powerW: finePeak.powerW,
    referenceCoarsePeakHz: coarsePeak.frequencyHz,
    tieThresholdW: FINE_PEAK_TIE_THRESHOLD_W,
  });
  options.onSuggestedFrequencyResolved?.(finePeak.frequencyHz);
  options.onStatus?.(
    `Suggested frequency ${formatFrequencyHz(finePeak.frequencyHz)} Hz is ready for comparison.`
  );

  const result: RetuneResult = {
    originalCenterHz: center,
    coarsePeakHz: coarsePeak.frequencyHz,
    finePeakHz: finePeak.frequencyHz,
    coarseSpec,
    fineSpec,
    coarseSweep,
    fineSweep,
    revisionId: selectedTarget.revision.revision_id,
    targetId: selectedTarget.target.target_id,
    targetSnapshot: selectedTarget.target,
  };

  options.onDebug?.('Retune completed', {
    revisionId: result.revisionId,
    targetId: result.targetId,
    originalCenterHz: result.originalCenterHz,
    coarsePeakHz: result.coarsePeakHz,
    finePeakHz: result.finePeakHz,
    awaitingUserConfirmation: true,
  });

  return result;
}

export function selectPeakByPower(
  points: SweepPoint[],
  referenceFrequencyHz: number,
  tieThresholdW: number
): SweepPoint {
  if (points.length === 0) {
    throw new Error('Cannot select a peak from an empty sweep.');
  }

  let best = points[0];

  for (let i = 1; i < points.length; i += 1) {
    const candidate = points[i];

    if (candidate.powerW > best.powerW + tieThresholdW) {
      best = candidate;
      continue;
    }

    if (Math.abs(candidate.powerW - best.powerW) <= tieThresholdW) {
      const candidateDistance = Math.abs(candidate.frequencyHz - referenceFrequencyHz);
      const bestDistance = Math.abs(best.frequencyHz - referenceFrequencyHz);
      if (candidateDistance < bestDistance) {
        best = candidate;
      }
    }
  }

  return best;
}

type SelectCoarseAnchorInput = {
  points: SweepPoint[];
  referenceFrequencyHz: number;
  tieThresholdW: number;
  exitCriteria: string;
  thresholdW: number;
};

function selectCoarseAnchorByExitCriteria(input: SelectCoarseAnchorInput): CoarseAnchorSelection {
  const normalized = normalizeExitCriteria(input.exitCriteria);
  const maxPeak = selectPeakByPower(input.points, input.referenceFrequencyHz, input.tieThresholdW);
  const qualifyingPoints = input.points.filter((point) => point.powerW > input.thresholdW);

  if (normalized.value === 'first_peak') {
    if (qualifyingPoints.length === 0) {
      return {
        point: maxPeak,
        source: 'max-peak-fallback',
        normalizedExitCriteria: normalized.value,
        unknownExitCriteriaFallback: normalized.unknown,
        thresholdW: input.thresholdW,
        qualifyingPointCount: 0,
      };
    }
    return {
      point: selectLowestFrequencyPoint(qualifyingPoints),
      source: 'first-peak',
      normalizedExitCriteria: normalized.value,
      unknownExitCriteriaFallback: normalized.unknown,
      thresholdW: input.thresholdW,
      qualifyingPointCount: qualifyingPoints.length,
    };
  }

  return {
    point: maxPeak,
    source: 'max-peak',
    normalizedExitCriteria: normalized.value,
    unknownExitCriteriaFallback: normalized.unknown,
    thresholdW: input.thresholdW,
    qualifyingPointCount: qualifyingPoints.length,
  };
}

function normalizeExitCriteria(value: string): { value: NormalizedExitCriteria; unknown: boolean } {
  const normalized = value.trim().toLowerCase();
  if (normalized === 'first_peak') {
    return { value: 'first_peak', unknown: false };
  }
  if (normalized === 'max_peak') {
    return { value: 'max_peak', unknown: false };
  }
  return { value: 'max_peak', unknown: true };
}

function selectLowestFrequencyPoint(points: SweepPoint[]): SweepPoint {
  let selected = points[0];
  for (let i = 1; i < points.length; i += 1) {
    const candidate = points[i];
    if (candidate.frequencyHz < selected.frequencyHz) {
      selected = candidate;
      continue;
    }
    if (candidate.frequencyHz === selected.frequencyHz && candidate.powerW > selected.powerW) {
      selected = candidate;
    }
  }
  return selected;
}

type CenterSelectionSource = 'selected-target-seed' | 'live-status' | 'status-command' | 'default';

type CenterSelection = {
  centerHz: number;
  source: CenterSelectionSource;
};

async function pickStartingCenterFrequency(
  transport: BleTransport,
  startingCenterHz?: number | null
): Promise<CenterSelection> {
  if (typeof startingCenterHz === 'number' && Number.isFinite(startingCenterHz)) {
    return {
      centerHz: clampHz(startingCenterHz),
      source: 'selected-target-seed',
    };
  }

  const info = transport.getDeviceInfo();
  const center = info?.centerFrequency;
  if (center && Number.isFinite(center)) {
    return {
      centerHz: clampHz(center),
      source: 'live-status',
    };
  }

  const current = info?.currentFrequency;
  if (current && Number.isFinite(current)) {
    return {
      centerHz: clampHz(current),
      source: 'live-status',
    };
  }

  try {
    const response = await transport.sendTextCommandWithResponse('getStatus', 2000, 180);
    const parsed = parseFrequencyFromGetStatus(response);
    if (parsed != null) {
      return {
        centerHz: clampHz(parsed),
        source: 'status-command',
      };
    }
  } catch {
    // Fall through to safe default below.
  }

  return {
    centerHz: RETUNE_DEFAULT_CENTER_HZ,
    source: 'default',
  };
}

function parseFrequencyFromGetStatus(response: string): number | null {
  const statusMatch = response.match(/Status:\\s*Frequency:\\s*(\\d+)/i);
  if (statusMatch) {
    const value = Number(statusMatch[1]);
    return Number.isFinite(value) ? value : null;
  }

  // Fallback for other firmware text variants.
  const genericMatch = response.match(/Frequency:\\s*(\\d+)/i);
  if (genericMatch) {
    const value = Number(genericMatch[1]);
    return Number.isFinite(value) ? value : null;
  }

  return null;
}

function clampHz(value: number): number {
  return Math.max(FREQUENCY_MIN_HZ, Math.min(FREQUENCY_MAX_HZ, Math.round(value)));
}

function formatFrequencyHz(value: number): string {
  return Math.round(value).toLocaleString('en-US');
}

function discardInitialSweepPoints(sweep: SweepResult, skipCount: number): SweepResult {
  if (skipCount <= 0 || sweep.points.length <= 1) {
    return sweep;
  }

  const clampedSkipCount = Math.min(skipCount, sweep.points.length - 1);
  if (clampedSkipCount <= 0) {
    return sweep;
  }

  return {
    ...sweep,
    points: sweep.points.slice(clampedSkipCount),
  };
}

function resolveSweepStartupPreroll(input: {
  configuredStartHz: number;
  configuredEndHz: number;
  stepHz: number;
  desiredPointCount: number;
}): { sweepStartHz: number; actualPointCount: number } {
  const direction: 1 | -1 = input.configuredEndHz >= input.configuredStartHz ? 1 : -1;
  const availableHz =
    direction > 0
      ? input.configuredStartHz - FREQUENCY_MIN_HZ
      : FREQUENCY_MAX_HZ - input.configuredStartHz;
  const availablePointCount = Math.max(0, Math.floor(availableHz / input.stepHz));
  const actualPointCount = Math.min(input.desiredPointCount, availablePointCount);

  return {
    sweepStartHz: input.configuredStartHz - direction * actualPointCount * input.stepHz,
    actualPointCount,
  };
}

function requirePositiveHz(value: number, targetId: string, fieldName: string): number {
  const rounded = Math.round(value);
  if (!Number.isFinite(rounded) || rounded <= 0) {
    throw new RetuneConfigError(
      'no_operational_targets',
      `Target "${targetId}" has invalid ${fieldName}.`
    );
  }
  return rounded;
}

function requireFiniteHz(value: number, targetId: string, fieldName: string): number {
  const rounded = Math.round(value);
  if (!Number.isFinite(rounded)) {
    throw new RetuneConfigError(
      'no_operational_targets',
      `Target "${targetId}" has invalid ${fieldName}.`
    );
  }
  return rounded;
}

function requireNonNegativeNumber(value: number, targetId: string, fieldName: string): number {
  if (!Number.isFinite(value) || value < 0) {
    throw new RetuneConfigError(
      'no_operational_targets',
      `Target "${targetId}" has invalid ${fieldName}.`
    );
  }
  return value;
}

function requireNonNegativeHz(value: number, targetId: string, fieldName: string): number {
  const rounded = Math.round(value);
  if (!Number.isFinite(rounded) || rounded < 0) {
    throw new RetuneConfigError(
      'no_operational_targets',
      `Target "${targetId}" has invalid ${fieldName}.`
    );
  }
  return rounded;
}

function requirePositiveMilliseconds(value: number, targetId: string, fieldName: string): number {
  const rounded = Math.round(value);
  if (!Number.isFinite(rounded) || rounded <= 0) {
    throw new RetuneConfigError(
      'no_operational_targets',
      `Target "${targetId}" has invalid ${fieldName}.`
    );
  }
  return rounded;
}
